import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { 
  getAdminProperties, 
  suspendProperty, 
  deleteProperty, 
  getAdminPropertyDetail,
  approveProperty,
  rejectProperty,
  migratePublishedToApproved 
} from '../../lib/api';
import { useAdminCounts } from '../../context/AdminCountsContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Building, 
  Ban, 
  ArrowRight, 
  Loader2, 
  Eye, 
  Trash2, 
  MapPin, 
  Bed, 
  Bath, 
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminProperties() {
  const { pendingReviewCount, refreshCounts } = useAdminCounts();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingProperty, setRejectingProperty] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);
  
  // Migration
  const [migrateLoading, setMigrateLoading] = useState(false);

  const loadProperties = async (cursor = null, status = statusFilter) => {
    try {
      const params = { limit: 20, cursor };
      if (status && status !== 'all') params.status = status;
      
      const response = await getAdminProperties(params);
      
      if (cursor) {
        setProperties(prev => [...prev, ...response.data.items]);
      } else {
        setProperties(response.data.items || []);
      }
      
      setNextCursor(response.data.next_cursor);
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Failed to load properties:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProperties();
  }, []);

  const handleStatusChange = (status) => {
    setStatusFilter(status);
    setLoading(true);
    loadProperties(null, status);
  };

  const handleApprove = async (propertyId) => {
    setActionLoading(propertyId);
    try {
      await approveProperty(propertyId);
      setProperties(prev => 
        prev.map(p => p.id === propertyId ? { ...p, status: 'approved' } : p)
      );
      toast.success('Property approved and now live!');
      // Refresh counts in sidebar badge
      refreshCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve property');
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectDialog = (property) => {
    setRejectingProperty(property);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!rejectionReason.trim() || rejectionReason.length < 10) {
      toast.error('Please provide a detailed rejection reason (at least 10 characters)');
      return;
    }
    
    setRejectLoading(true);
    try {
      await rejectProperty(rejectingProperty.id, rejectionReason);
      setProperties(prev => 
        prev.map(p => p.id === rejectingProperty.id 
          ? { ...p, status: 'rejected', rejection_reason: rejectionReason } 
          : p
        )
      );
      toast.success('Property rejected. Host has been notified.');
      setRejectDialogOpen(false);
      setRejectingProperty(null);
      setRejectionReason('');
      // Refresh counts in sidebar badge
      refreshCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject property');
    } finally {
      setRejectLoading(false);
    }
  };

  const handleSuspend = async (propertyId) => {
    if (!window.confirm('Are you sure you want to suspend this property?')) return;
    
    setActionLoading(propertyId);
    try {
      await suspendProperty(propertyId);
      setProperties(prev => 
        prev.map(p => p.id === propertyId ? { ...p, status: 'suspended' } : p)
      );
      toast.success('Property suspended');
    } catch (error) {
      toast.error('Failed to suspend property');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (propertyId) => {
    if (!window.confirm('Are you sure you want to DELETE this property? This cannot be undone.')) return;
    
    setActionLoading(propertyId);
    try {
      await deleteProperty(propertyId);
      setProperties(prev => prev.filter(p => p.id !== propertyId));
      toast.success('Property deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete property');
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewDetail = async (propertyId) => {
    setDetailLoading(true);
    setDetailDialogOpen(true);
    try {
      const res = await getAdminPropertyDetail(propertyId);
      setSelectedProperty(res.data);
    } catch (error) {
      toast.error('Failed to load property details');
      setDetailDialogOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleMigrate = async () => {
    if (!window.confirm('This will convert all "published" properties to "approved" status. Continue?')) return;
    
    setMigrateLoading(true);
    try {
      const res = await migratePublishedToApproved();
      toast.success(res.data.message);
      // Reload properties and refresh counts
      setLoading(true);
      loadProperties(null, statusFilter);
      refreshCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Migration failed');
    } finally {
      setMigrateLoading(false);
    }
  };

  const formatPrice = (cents) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: { className: 'bg-slate-100 text-slate-700', icon: null },
      pending_review: { className: 'bg-amber-100 text-amber-700', icon: Clock },
      approved: { className: 'bg-green-100 text-green-700', icon: CheckCircle },
      rejected: { className: 'bg-red-100 text-red-700', icon: XCircle },
      published: { className: 'bg-green-100 text-green-700', icon: CheckCircle },
      paused: { className: 'bg-slate-200 text-slate-600', icon: null },
      suspended: { className: 'bg-red-200 text-red-800', icon: Ban },
      deleted: { className: 'bg-red-200 text-red-800', icon: Trash2 }
    };
    return variants[status] || { className: 'bg-slate-100 text-slate-700', icon: null };
  };

  return (
    <div className="space-y-6" data-testid="admin-properties">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Properties</h1>
          <p className="text-slate-500 mt-1">
            Manage platform listings and approve new properties
            {pendingReviewCount > 0 && (
              <span className="ml-2 text-amber-600 font-medium" data-testid="header-pending-count">
                • {pendingReviewCount} pending review
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Migration Button - only show if there might be published properties */}
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleMigrate}
            disabled={migrateLoading}
            className="text-xs"
            data-testid="migrate-btn"
          >
            {migrateLoading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-1" />
            )}
            Migrate Published→Approved
          </Button>
          
          <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-48" data-testid="status-filter">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending_review">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  Pending Review
                </span>
              </SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="published">Published (Legacy)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Pending Review Alert */}
      {pendingReviewCount > 0 && statusFilter !== 'pending_review' && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-amber-800" data-testid="pending-alert-count">{pendingReviewCount} properties awaiting review</p>
                <p className="text-sm text-amber-600">Review and approve or reject these listings</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={() => handleStatusChange('pending_review')}
              data-testid="view-pending-btn"
            >
              View Pending
            </Button>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : properties.length > 0 ? (
        <>
          <div className="space-y-4">
            {properties.map((property) => {
              const statusInfo = getStatusBadge(property.status);
              const StatusIcon = statusInfo.icon;
              const isPendingReview = property.status === 'pending_review';
              
              return (
                <Card 
                  key={property.id} 
                  className={isPendingReview ? 'border-amber-300 bg-amber-50/50' : ''}
                  data-testid={`property-row-${property.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium text-slate-900">{property.title}</h3>
                          <Badge className={`${statusInfo.className} gap-1`}>
                            {StatusIcon && <StatusIcon className="w-3 h-3" />}
                            {property.status === 'pending_review' ? 'Pending Review' : property.status}
                          </Badge>
                          {isPendingReview && (
                            <Badge className="bg-amber-500 text-white">Action Required</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          {property.city_id} • {property.beds} beds • {property.baths} baths
                        </p>
                        <p className="text-sm text-slate-400">
                          Host: {property.host_name} ({property.host_email})
                        </p>
                        <p className="text-xs text-slate-400">
                          Created {format(new Date(property.created_at), 'MMM d, yyyy')}
                          {property.submitted_at && ` • Submitted ${format(new Date(property.submitted_at), 'MMM d, yyyy')}`}
                        </p>
                        
                        {/* Show rejection reason if rejected */}
                        {property.status === 'rejected' && property.rejection_reason && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                            <strong>Rejection reason:</strong> {property.rejection_reason}
                          </div>
                        )}
                      </div>
                      
                      <div className="text-right space-y-3">
                        <p className="text-lg font-semibold text-slate-900">
                          {formatPrice(property.base_price_nightly)}/night
                        </p>
                        
                        <div className="flex items-center gap-2 justify-end flex-wrap">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetail(property.id)}
                            data-testid={`view-btn-${property.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          
                          {/* Approve/Reject buttons for pending review */}
                          {isPendingReview && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleApprove(property.id)}
                                disabled={actionLoading === property.id}
                                data-testid={`approve-btn-${property.id}`}
                              >
                                {actionLoading === property.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Approve
                                  </>
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => openRejectDialog(property)}
                                disabled={actionLoading === property.id}
                                data-testid={`reject-btn-${property.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          
                          {/* Suspend for approved/published properties */}
                          {['approved', 'published'].includes(property.status) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSuspend(property.id)}
                              disabled={actionLoading === property.id}
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            >
                              {actionLoading === property.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Ban className="w-4 h-4 mr-1" />
                                  Suspend
                                </>
                              )}
                            </Button>
                          )}
                          
                          {/* Delete for any status except already deleted */}
                          {property.status !== 'deleted' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(property.id)}
                              disabled={actionLoading === property.id}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-btn-${property.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {hasMore && (
            <div className="text-center">
              <Button 
                variant="outline"
                onClick={() => loadProperties(nextCursor)}
              >
                Load More
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Building className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
              No properties found
            </h3>
            <p className="text-slate-500">
              {statusFilter ? `No ${statusFilter.replace('_', ' ')} properties found.` : 'No properties listed yet.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Property Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="property-detail-dialog">
          <DialogHeader>
            <DialogTitle>Property Details</DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : selectedProperty ? (
            <div className="space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-lg font-semibold">{selectedProperty.title}</h3>
                <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {selectedProperty.city_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Bed className="w-4 h-4" />
                    {selectedProperty.beds} beds
                  </span>
                  <span className="flex items-center gap-1">
                    <Bath className="w-4 h-4" />
                    {selectedProperty.baths} baths
                  </span>
                  <span className="flex items-center gap-1">
                    <DollarSign className="w-4 h-4" />
                    {formatPrice(selectedProperty.base_price_nightly)}/night
                  </span>
                </div>
                <Badge className={`mt-2 ${getStatusBadge(selectedProperty.status).className}`}>
                  {selectedProperty.status}
                </Badge>
              </div>

              {/* Host Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium">Host</p>
                <p className="text-sm text-slate-600">
                  {selectedProperty.host?.name} ({selectedProperty.host?.email})
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Host Status: {selectedProperty.host?.host_status || 'pending'}
                </p>
              </div>

              {/* Description */}
              {selectedProperty.description && (
                <div>
                  <p className="text-sm font-medium mb-1">Description</p>
                  <p className="text-sm text-slate-600">{selectedProperty.description}</p>
                </div>
              )}

              {/* Amenities */}
              {selectedProperty.amenities?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Amenities</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedProperty.amenities.map(a => (
                      <Badge key={a} variant="outline">{a.replace(/_/g, ' ')}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Photos */}
              {selectedProperty.images?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Photos ({selectedProperty.images.length})</p>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedProperty.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={`${API_URL}${img.thumbnail_url || img.path_thumb || img.url}`}
                        alt={`Photo ${idx + 1}`}
                        className="w-full h-24 object-cover rounded"
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Action buttons in detail view for pending */}
              {selectedProperty.status === 'pending_review' && (
                <div className="pt-4 border-t flex gap-3">
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white flex-1"
                    onClick={() => {
                      handleApprove(selectedProperty.id);
                      setDetailDialogOpen(false);
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve Property
                  </Button>
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-1"
                    onClick={() => {
                      setDetailDialogOpen(false);
                      openRejectDialog(selectedProperty);
                    }}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject Property
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <XCircle className="w-5 h-5" />
              Reject Property
            </DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting "{rejectingProperty?.title}". This will be sent to the host.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg text-amber-800 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>The host will receive an email with your feedback and can edit and resubmit their listing.</p>
            </div>
            
            <Textarea
              placeholder="Please describe what needs to be changed or improved..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
              className="resize-none"
              data-testid="rejection-reason-input"
            />
            <p className="text-xs text-slate-500">
              {rejectionReason.length}/1000 characters (minimum 10)
            </p>
          </div>
          
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setRejectDialogOpen(false)}
              disabled={rejectLoading}
            >
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleReject}
              disabled={rejectLoading || rejectionReason.length < 10}
              data-testid="confirm-reject-btn"
            >
              {rejectLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Reject Property
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
